import React, { useMemo, useState, useEffect } from 'react';

import { MobileSLAHeroProps } from './MobileSLAHero.props';
import {
  HeroWrapper,
  HeroContainer,
  RadialTimerContainer,
  TimerSvg,
  TimerContent,
  TimeRemaining,
  TimeLabel,
  SLABreakdown,
  SLARow,
  SLARowLabel,
  SLARowValue,
  SLABar,
  SLABarFill,
} from './MobileSLAHero.styles';
import { formatDuration, getAttendanceSLAs } from './MobileSLAHero.utils';
import { COLORS } from '../../../../../configs/colors';

export const MobileSLAHero: React.FC<MobileSLAHeroProps> = ({ service, serviceSLAs }) => {
  // Tick every 60s so remaining time updates live
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  const { attendance, resolution } = useMemo(
    () => getAttendanceSLAs(serviceSLAs, service.clientSLA, service.opsSLA),
    [serviceSLAs, service.clientSLA, service.opsSLA, tick]
  );
  const clientSLA = resolution;
  const opsSLA = attendance;

  const radius = 44;
  const strokeWidth = 7;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const strokeDashoffset = circumference - (clientSLA.percentage / 100) * circumference;

  const responseText = opsSLA.isMet
    ? 'Met'
    : opsSLA.isBreached
      ? 'Breached'
      : opsSLA.remainingMs > 0
        ? `${formatDuration(opsSLA.remainingMs)} left`
        : 'N/A';

  const responseColor = opsSLA.isMet
    ? COLORS.SLAComplete
    : opsSLA.isBreached
      ? COLORS.SLABreached
      : opsSLA.statusColor;

  const resolutionText = clientSLA.isMet
    ? 'Met'
    : clientSLA.isBreached
      ? 'Breached'
      : clientSLA.remainingMs > 0
        ? `${formatDuration(clientSLA.remainingMs)} left`
        : 'N/A';

  const resolutionColor = clientSLA.statusColor;

  return (
    <HeroWrapper>
      <HeroContainer>
        <RadialTimerContainer>
          <TimerSvg width="110" height="110" viewBox="0 0 110 110">
            <defs>
              <linearGradient id="sla-hero-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={clientSLA.gradientColors[0]} />
                <stop offset="100%" stopColor={clientSLA.gradientColors[1]} />
              </linearGradient>
            </defs>
            <circle
              stroke={COLORS.SLATrackGrey}
              fill="transparent"
              strokeWidth={strokeWidth}
              r={normalizedRadius}
              cx="55"
              cy="55"
            />
            <circle
              stroke="url(#sla-hero-grad)"
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              r={normalizedRadius}
              cx="55"
              cy="55"
            />
          </TimerSvg>
          <TimerContent>
            <TimeRemaining sx={{ color: clientSLA.statusColor }}>
              {clientSLA.timeText}
            </TimeRemaining>
            <TimeLabel>Remaining</TimeLabel>
          </TimerContent>
        </RadialTimerContainer>

        <SLABreakdown>
          <SLARow>
            <SLARowLabel>Attendance</SLARowLabel>
            <SLARowValue sx={{ color: responseColor }}>{responseText}</SLARowValue>
          </SLARow>
          <SLARow>
            <SLARowLabel>Resolution</SLARowLabel>
            <SLARowValue sx={{ color: resolutionColor }}>{resolutionText}</SLARowValue>
          </SLARow>
          <SLABar>
            <SLABarFill sx={{ width: `${clientSLA.percentage}%` }} />
          </SLABar>
        </SLABreakdown>
      </HeroContainer>
    </HeroWrapper>
  );
};
